/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKDR Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Mock run-envelope fixtures for the Agent Runner UI.
 *
 * Shape exactly mirrors the SkillAgent contract documented in
 * services/core-api/src/routes/admin/agents.js. Used by:
 *   (1) the "Try mock data" button so the page is usable before
 *       SKILLAGENT_BASE_URL is configured;
 *   (2) the component test that asserts the renderers light up.
 */

export const MOCK_AGENTS = [
  { id: 'wms/wms-ops', displayName: 'WMS Ops Agent' },
  { id: 'wms/wms-persona-agent', displayName: 'WMS Persona Agent' },
];

export const MOCK_RUN_ENVELOPE = {
  summary: {
    runStatus: 'done',
    executedTools: ['knowledge.search', 'reasoning.run'],
    failedTools: [],
    inferenceOutputMode: 'json',
  },
  runtime: {
    toolPolicy: [
      {
        toolId: 'knowledge.search',
        actionClass: 'read',
        // Mirrors the real envelope: request.policyContext.actionClass is the
        // nested twin of the top-level actionClass.
        request: {
          policyContext: { actionClass: 'read' },
        },
        // decision is an OBJECT in the real envelope (not a bare string).
        // The string verdict lives at decision.decision; rendering the whole
        // object as a React child throws React error #31.
        decision: {
          contractName: 'tool.invocation',
          contractVersion: '1.0',
          decisionId: 'dec_mock_001',
          requestId: 'req_mock_001',
          toolId: 'knowledge.search',
          decision: 'allowed',
          reasonCode: 'POLICY_ALLOW',
          decidedAt: '2026-06-27T00:00:00Z',
          schemaId: 'policy.decision.v1',
          requiresApproval: false,
        },
        result: {
          status: 'success',
          decision: 'allowed',
        },
        output: {
          context:
            'Putaway logic groups SKUs by velocity class. A-class slots are reserved '
            + 'for the top 20% of SKUs by throughput, placed in the golden zone '
            + '(eye-to-hip pick face). B/C classes fan out from there.',
          sources: [
            {
              title: 'WMS Putaway Algorithm — Velocity Slotting',
              docId: '6a2a6d339ce26a7f6caeca21',
              similarity: 0.842,
              snippet:
                'A-class SKUs to forward pick face. Calculation: rolling 30-day '
                + 'throughput rank percentile-binned into A (top 20%), B (next 30%), '
                + 'C (bottom 50%).',
            },
            {
              title: 'Manhattan WMi Slotting Reference',
              docId: '6a117862cb8477efd453e8e9',
              similarity: 0.781,
              snippet:
                'Slot eligibility flags (height, weight, hazmat) override velocity '
                + 'class. ABC bins are advisory; physical fit is hard constraint.',
            },
          ],
          chunksFound: 2,
        },
      },
      {
        // reasoning.run lands as a toolPolicy entry too. output.final_text is
        // the synthesised practitioner-voice answer (\n\n-separated paragraphs);
        // stage_outputs is the per-stage trace (plan / draft / review / etc.).
        toolId: 'reasoning.run',
        actionClass: 'invoke',
        request: { policyContext: { actionClass: 'invoke' } },
        decision: {
          contractName: 'tool.invocation',
          contractVersion: '1.0',
          decisionId: 'dec_mock_002',
          requestId: 'req_mock_002',
          toolId: 'reasoning.run',
          decision: 'allowed',
          reasonCode: 'POLICY_ALLOW',
          decidedAt: '2026-06-27T00:00:01Z',
          schemaId: 'policy.decision.v1',
          requiresApproval: false,
        },
        result: { status: 'success', decision: 'allowed' },
        output: {
          workflow_id: 'wms_operational_consultation',
          run_id: 'wfr_mock_reasoning_001',
          state: 'completed',
          final_text:
            'Velocity-class slotting compresses putaway time because it puts the SKUs '
            + 'that move most often closest to the dock door — the picker walks '
            + 'less, the forklift turns less, and aisle congestion drops on the '
            + 'busiest lanes.\n\n'
            + 'In practice, A-class SKUs (top 20% of throughput) land in the golden '
            + 'zone — eye-to-hip pick face, forward of the dock. B and C fan out '
            + 'from there. The win shows up twice: once on putaway (shorter travel '
            + 'to slot), and again on pick (shorter travel to pull). Our last '
            + 'comparison run measured 531.1 min baseline against 257.4 min '
            + 'optimised — a ~2.1× reduction on putaway alone.\n\n'
            + 'Caveat: ABC bins are advisory. Slot eligibility (height, weight, '
            + 'hazmat) is a hard constraint and overrides the velocity tier. So '
            + 'expect ~5–10% of A-class SKUs to fall outside the golden zone for '
            + 'physical-fit reasons, and design the slotting algorithm to surface '
            + 'those overrides rather than fight them.',
          stage_outputs: [
            {
              stage_id: 'plan',
              archetype: 'plan_review_synthesize',
              fallback_tier: 'claude',
              llm_text:
                'PLAN: 1. Ground in WMS putaway KB (knowledge.search). '
                + '2. Recall last comparison-run numbers from persisted memory. '
                + '3. Draft a practitioner-voice answer covering mechanism + numbers + caveat.',
            },
            {
              stage_id: 'draft',
              archetype: 'plan_review_synthesize',
              fallback_tier: 'claude',
              llm_text:
                'DRAFT: Velocity-class slotting compresses putaway time because the high-velocity SKUs land closest to the dock. '
                + 'A-class in the golden zone, B/C fan out. Measured 531.1→257.4 min on the last run.',
            },
            {
              stage_id: 'synthesize',
              archetype: 'plan_review_synthesize',
              fallback_tier: 'claude',
              llm_text:
                'SYNTHESIS: kept the mechanism + numbers + the physical-fit caveat. '
                + 'Reads like a WMS practitioner explaining to a junior peer.',
            },
          ],
        },
      },
    ],
    memory: {
      read: {
        result: {
          records: [
            {
              key: 'wms.putaway.last_run.summary',
              content:
                'Last putaway-comparison run: 531.1 minutes baseline vs 257.4 minutes '
                + 'optimised. 5.3-5.7× live-engine speedup observed.',
              source: 'persisted_memory',
            },
            {
              key: 'persona.wms_ops.goals.primary',
              content:
                'Reduce average putaway cycle time without sacrificing pick-face hit '
                + 'rate on A-class SKUs.',
              source: 'persona_record',
            },
          ],
        },
      },
    },
  },
  agentId: 'wms/wms-ops',
  goal: '(mock) Explain how velocity-class slotting reduces putaway time.',
  meta: {
    note: 'This is a mocked envelope. SkillAgent HTTP service is not yet deployed.',
  },
};

export const MOCK_RUN_ENVELOPE_AWAITING_APPROVAL = {
  ...MOCK_RUN_ENVELOPE,
  summary: {
    ...MOCK_RUN_ENVELOPE.summary,
    runStatus: 'awaiting_approval',
  },
};

export const MOCK_RUN_ENVELOPE_ERROR = {
  summary: {
    runStatus: 'error',
    executedTools: [],
    failedTools: ['knowledge.search'],
    inferenceOutputMode: 'json',
  },
  runtime: {
    toolPolicy: [
      {
        toolId: 'knowledge.search',
        actionClass: 'read',
        request: { policyContext: { actionClass: 'read' } },
        decision: {
          decision: 'denied',
          reasonCode: 'PROVIDER_NOT_REGISTERED',
          requiresApproval: false,
        },
        result: { status: 'error', decision: 'denied' },
        output: { error: 'Provider not registered: knowledge.search' },
      },
    ],
    memory: { read: { result: { records: [] } } },
  },
  error: 'Tool knowledge.search failed: provider not registered',
};
